/**
 * How RDS's refusals are worded for the person who has to act on them,
 * and the preconditions RDS reports before it will change where PHI rows
 * come from.
 *
 * Extracted from RdsHealthPanel.js so the migration layout and the legacy
 * panel share ONE implementation of the wording and the checklist.
 */
import { HStack, Badge, Text } from "@chakra-ui/react";
import { PhiClientError } from "../../../utils/rdsPhi";
import { formatAge } from "../../../utils/resultCache";

/** The typed phrases RDS demands before it changes where PHI rows come from
 *  (source_mode.CONFIRM_PHRASES). Typing the word, not clicking a button,
 *  is the whole safeguard on a switch that stops the mirror. */
export const CONFIRM_INDEPENDENT = "INDEPENDENT";
export const CONFIRM_MIRROR = "MIRROR";
export const CONFIRM_SUPABASE_ONLY = "SUPABASE";

/** What RDS's refusal means to the person clicking, PHI-free. */
export function operatorErrorMessage(err) {
  if (err instanceof PhiClientError) {
    if (err.statusCode === 401) return "RDS did not accept this session — sign in again.";
    if (err.statusCode === 403) return "RDS refused: this account is not an RDS operator.";
    if (err.statusCode === 503) return `RDS operator access unavailable${err.detail ? `: ${err.detail}` : ""}.`;
    if (err.statusCode === 409) {
      // Not every 409 is the sync-mirror lock. rdsPhi annotates the error
      // from RDS's body: `forced` = the flag is held while independent
      // (flipping it needs force + a note on RDS's own admin page),
      // `refused` = RDS declined this run and `detail` says why.
      // Only a plain 409 is "a sync is already running".
      const detail = typeof err.detail === "string" ? err.detail : "";
      if (err.forced) {
        return `${detail || "RDS holds this flag while independent"} — use the RDS admin page's Force off with a note.`;
      }
      if (err.refused) return detail || "RDS refused this request.";
      return "A mirror sync is already running — try again in a minute.";
    }
    return `RDS returned ${err.statusCode}${typeof err.detail === "string" && err.detail ? `: ${err.detail}` : ""}.`;
  }
  return err?.message || "RDS could not be reached.";
}

/**
 * The 403 that these routes return means "operator, but not superadmin" --
 * RDS's _require_operator let the call through and the route's own
 * superadmin check refused it -- so the generic "not an RDS operator"
 * wording would send the person looking at the wrong list. A 409 carries
 * RDS's own detail (and, when RDS lists them, the blockers) verbatim: it
 * is the one message that says what to fix.
 */
export function sourceModeErrorMessage(err) {
  if (err instanceof PhiClientError) {
    if (err.statusCode === 403) return "requires superadmin role in RDS";
    if (err.statusCode === 409) {
      const blockers = Array.isArray(err.blockers) ? err.blockers : [];
      return `${err.detail || "RDS refused the change"}${blockers.length ? ` — ${blockers.join("; ")}` : ""}`;
    }
  }
  return operatorErrorMessage(err);
}

/** A green/red row for one precondition RDS reported. */
export function CheckRow({ ok, label, detail }) {
  return (
    <HStack fontSize="xs" spacing={2} align="flex-start">
      <Badge colorScheme={ok ? "green" : "red"} fontSize="10px" minW="34px" textAlign="center">
        {ok ? "OK" : "NO"}
      </Badge>
      <Text color={ok ? "gray.700" : "red.700"}>
        {label}{detail ? ` — ${detail}` : ""}
      </Text>
    </HStack>
  );
}

/** Distinct table names behind `tables_without_id_pk` ("schema.table"). */
export function repairableTables(p) {
  const out = [];
  (p?.tables_without_id_pk || []).forEach((entry) => {
    const dot = String(entry).indexOf(".");
    const table = dot < 0 ? String(entry) : String(entry).slice(dot + 1);
    if (table && !out.includes(table)) out.push(table);
  });
  return out;
}

/**
 * The readable form of GET /admin/source-mode's `preconditions`. The
 * authoritative verdict is RDS's `blockers` list (rendered verbatim next to
 * the checks); these rows only make the green side visible too, so an
 * operator sees what already passes rather than an empty list.
 */
export function preconditionRows(p) {
  if (!p) return [];
  const run = p.last_mirror_run || {};
  const noPk = p.tables_without_id_pk || [];
  return [
    { label: "RDS auth enforced", ok: !!p.auth_enforced },
    { label: "Kill switch off", ok: !p.kill_switch },
    { label: "Not pinned by RDS_SOURCE_MODE", ok: !p.pinned },
    { label: "Sync scheduler thread running", ok: !!p.scheduler_alive },
    { label: "No mirror sync in progress", ok: !p.mirror_busy },
    { label: "Every PHI table keyed on id", ok: noPk.length === 0, detail: noPk.join(", ") },
    {
      label: "Last mirror run ok and under 10 minutes old",
      ok: run.ok === true && run.age_seconds != null && run.age_seconds <= 600,
      detail: run.finished_at ? `last run ${run.ok ? "ok" : "FAILED"} ${formatAge(run.finished_at)}` : "no run recorded",
    },
    { label: "Overlay flags effective and settled", ok: !!p.overlays_ready },
  ];
}
